import { requestBuilder } from '../../../http/RequestBuilder';

export interface IncomeSplitProgressDto {
    percentage: number;
    cents: number;
    targetAmount: number;
}

export interface IncomeSplitProgressByCategoryDto {
    bills: IncomeSplitProgressDto;
    spend: IncomeSplitProgressDto;
    goals: IncomeSplitProgressDto;
}

export interface IncomeSplitExecutionTransactionItemDto {
    id: string;
    incomeSplitExecutionId: string;
    transactionUuid?: string;
    membershipId: string;
    sourceAccountId: string;
    destinationAccountId: string;
    sourceAccountDisplayName?: string;
    destinationAccountDisplayName?: string;
    destinationAccountInstitutionName?: string | null;
    destinationAccountLogo?: string | null;
    destinationAccountImgBase64?: string | null;
    destinationAccountNumberMasked?: string | null;
    destinationAccountIcon?: string | null;
    destinationAccountColor?: string | null;
    amount: number;
    status: string;
    transferType?: string;
    memo?: string;
    failReason?: string;
    ruleId?: string;
    originalRuleId?: string;
    dateAdded: string;
    createdAt: string;
    updatedAt?: string;
    balanceAfterTransaction: number;
    mappedStatus?: string;
    ictDepositStatus?: string | null;
    matchingTransfer?: {
        status?: string;
        transferPlan?: {
            destinationAccountDetails?: IncomeSplitExecutionDestinationAccountDetailsDto;
            destinationDepositoryAccountDetails?: IncomeSplitExecutionDestinationAccountDetailsDto;
            destinationInvestmentAccountDetails?: IncomeSplitExecutionDestinationAccountDetailsDto;
        };
    };
}

interface IncomeSplitExecutionDestinationAccountDetailsDto {
    accountNumber?: string | null;
    accountNumberMasked?: string | null;
    institutionName?: string | null;
    logo?: string | null;
    imgBase64?: string | null;
    icon?: string | null;
    color?: string | null;
}

export interface IncomeSplitExecutionHistoryItemDto {
    id: string;
    configId: string;
    date: string;
    amount: number;
    description: string;
    sourceAccountName?: string;
    transfers: IncomeSplitExecutionTransactionItemDto[];
    progress: IncomeSplitProgressByCategoryDto;
    thisExecutionProgress?: IncomeSplitProgressByCategoryDto;
    executionFromConfigSourcedFromMoneyMap: boolean;
    moneyMapId?: string;
}

export interface IncomeSplitMonthlyHistoryDto {
    month: string;
    executions: IncomeSplitExecutionHistoryItemDto[];
    progress: IncomeSplitProgressByCategoryDto;
    noMoneyMapPresentForProgress?: boolean;
}

export interface IncomeSplitOverviewDto {
    configs: unknown[];
    history: IncomeSplitMonthlyHistoryDto[];
}

const useGetIncomeSplitOverview = requestBuilder.query<
    IncomeSplitOverviewDto,
    { membershipId: string }
>({
    endpoint: ({ membershipId }) =>
        `/api/admin/money-map/${membershipId}/income-split-overview`,
    queryKey: ({ membershipId }) => `getIncomeSplitOverview/${membershipId}`,
});

interface UseIncomeSplitOverview {
    membershipId: string | null;
    data: IncomeSplitOverviewDto | undefined;
    isLoading: boolean;
    isError: boolean;
}

export const useIncomeSplitOverview = (
    membershipId: string | null,
): UseIncomeSplitOverview => {
    const { data, isLoading, isError } = useGetIncomeSplitOverview(
        { membershipId: membershipId ?? '' },
        {
            enabled: membershipId != null,
        },
    );

    return {
        membershipId,
        data,
        isLoading,
        isError,
    };
};
